import { createApi, fetchBaseQuery, FetchBaseQueryError, BaseQueryFn, FetchArgs } from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import { 
  GlobalSettings,
  Neighborhood,
  User, 
  Place, 
  Location,
  IdentityOptions, 
  NotificationTestResponse,
  CreateMockPostsRequest, 
  CreateMockPostsResponse,
  Post,
  PostStats,
  MockPostJobStatus
} from '../types/interfaces';
import { clearAuthCookies } from '../utils/cookie';

/** A person behind an audience stat, with derived activity detail. */
export type ActivityLevel = 'power' | 'regular' | 'occasional' | 'dormant' | 'new';
export type WindowBehavior = 'posted' | 'messaged' | 'lurked';

// --- SMS / sign-in tracking (AuthEvent audit log) ---
export type AuthEventWindow = 'hour' | 'today' | 'day' | 'week' | 'month';

export interface AuthEventWindowStats {
  codesSent: number;
  smsSent: number;
  verified: number;
  failed: number;
  logins: number;
  registrations: number;
}

export interface AuthAbuser {
  phone: string;
  userId: string | null;
  displayName: string | null;
  username: string | null;
  codesSent: number;
  smsSent: number;
  verified: number;
  failed: number;
  logins: number;
  distinctIps: number;
  firstAt: string;
  lastAt: string;
}

export interface AuthEventRow {
  _id: string;
  type: string;
  phone: string | null;
  userId: string | null;
  displayName: string | null;
  username: string | null;
  smsSent: boolean | null;
  failureReason: string | null;
  ip: string | null;
  createdAt: string;
}
export interface AudienceMember {
  _id: string;
  displayName: string;
  username: string;
  profilePicture: string | null;
  facePicture: string | null;
  publicPicture: string | null;
  createdAt: string;
  lastActive: string | null;
  isOnline: boolean;
  profileCompleted: boolean;
  pushable: boolean;
  authProvider: string;
  neighborhood: string | null;
  city: string | null;
  state: string | null;
  postCount: number;
  lastPostAt: string | null;
  messageCount: number;
  lastMessageAt: string | null;
  postsInWindow: number;
  messagesInWindow: number;
  windowBehavior: WindowBehavior;
  accountAgeDays: number;
  postsPerWeek: number;
  activityLevel: ActivityLevel;
  isMockUser: boolean;
}

/** A notable person tied to an enriched entity (lead actor, artist, author…). */
export interface EnrichedPerson {
  name: string;
  role?: string;
}

/** Reusable "riff" material the In Common opener engines compose from.
 *  All riff pools are arrays — supply as many options as you like. */
export interface EnrichmentHooks {
  catchphrases?: string[];
  quotableLines?: string[];
  signatureLyrics?: string[];
  eras?: string[];
  personaTag?: string;
  openers1v1?: string[];
  openersRoom?: string[];
}

/** Mirror of the backend EnrichedEntity model (see EnrichedEntity.model.ts). */
export interface EnrichedEntity {
  _id: string;
  kind: string;
  valueKey: string;
  valueDisplay: string;
  status: 'resolved' | 'unresolved';
  source: string;
  facts?: string[];
  descriptor?: string;
  people?: EnrichedPerson[];
  year?: number;
  genres?: string[];
  hooks?: EnrichmentHooks;
  externalId?: string;
  reviewed?: boolean;
  reviewedAt?: string;
  fetchedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Interest opener copy (fixed catalog) — separate from media enrichment. */
export type InterestCopyScope = 'interest' | 'category' | 'general';

export interface InterestCopyRow {
  _id: string;
  scope: InterestCopyScope;
  key: string;
  displayLabel: string;
  openers1v1: string[];
  openersRoom: string[];
  reviewed: boolean;
  reviewedAt?: string;
  updatedAt?: string;
}

export interface InterestCatalog {
  categories: Array<{ key: string; label: string; emoji: string; options: string[] }>;
}

// Create a mutex to prevent multiple token refresh calls
const mutex = new Mutex();

// Function to get the current API base URL (checks localStorage dynamically)
const getApiBaseUrl = (): string => {
  // Check localStorage first - if explicitly set, use that value
  // Only fall back to environment variable if localStorage is not set
  const localStorageValue = localStorage.getItem('useRemoteApi');
  const useRemoteApi = localStorageValue !== null 
    ? localStorageValue === 'true'  // Use localStorage value if it exists
    : (process.env.REACT_APP_USE_REMOTE_API === 'true'); // Fall back to env var only if localStorage not set
  
  const remoteUrl = process.env.REACT_APP_REMOTE_API_URL || 'https://api.gridappsocial.com';
  const localUrl = process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:3000';
  const selectedUrl = useRemoteApi ? remoteUrl : localUrl;
  
  // Log for debugging
  console.log('[Admin API] getApiBaseUrl:', {
    useRemoteApi,
    selectedUrl,
    localStorageValue,
    envVarValue: process.env.REACT_APP_USE_REMOTE_API,
  });
  
  return selectedUrl;
};

// Initial API base URL (will be updated dynamically)
const apiBaseUrl = getApiBaseUrl();
console.log(`Using API URL: ${apiBaseUrl} (Remote API: ${localStorage.getItem('useRemoteApi') === 'true' ? 'Enabled' : 'Disabled'})`);

// Custom base query wrapper that checks localStorage dynamically for API URL
const dynamicBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // Get current API base URL (checks localStorage dynamically)
  const currentApiBaseUrl = getApiBaseUrl();
  
  // Create a new baseQuery with the current URL if it changed
  // HttpOnly cookies are automatically sent with credentials: 'include'
  const currentBaseQuery = fetchBaseQuery({
    baseUrl: `${currentApiBaseUrl}/api/`,
    credentials: 'include', // Required to send HttpOnly cookies with requests
    // No Authorization header needed - tokens are in HttpOnly cookies
  });
  
  return currentBaseQuery(args, api, extraOptions);
};

// Custom base query with token refresh logic
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // Wait until the mutex is available without locking it
  await mutex.waitForUnlock();
  
  // Use dynamic base query that checks localStorage for API URL
  let result = await dynamicBaseQuery(args, api, extraOptions);
  
  if (result.error && result.error.status === 401) {
    // Try to refresh token using HttpOnly cookies
    // Refresh token is automatically sent via credentials: 'include'
    const release = await mutex.acquire();
    
    try {
      // Refresh token endpoint reads from HttpOnly cookies automatically
      const refreshResult = await dynamicBaseQuery({
        url: 'auth/refresh-token',
        method: 'POST',
        // No body needed - refresh token is in HttpOnly cookie
      }, api, extraOptions);
      
      if (refreshResult.data) {
        // Tokens are set as HttpOnly cookies by the backend
        // No need to store in localStorage
        // Retry the original request
        result = await dynamicBaseQuery(args, api, extraOptions);
      } else {
        // Refresh failed, redirect to login
        // Clear any old localStorage tokens (cleanup)
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('lastTokenTime');
        clearAuthCookies();
        window.location.href = '/login';
      }
    } finally {
      // Release the mutex
      release();
    }
  }
  
  return result;
};

// Proactive refresh no longer needed - HttpOnly cookies are managed by backend
// Token refresh happens automatically on 401 errors via baseQueryWithReauth

export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['GlobalSettings', 'IdentityOptions', 'Notifications', 'Posts', 'Locations', 'Users', 'MockPosts', 'Places', 'Security', 'Analytics', 'PlaceClaims', 'WelcomeDm', 'Enrichment', 'InterestCopy'] as const,
  endpoints: (build) => ({
    getGlobalSettings: build.query<GlobalSettings, void>({
      query: () => 'admin/settings',
      providesTags: ['GlobalSettings'],
    }),
    updateGlobalSettings: build.mutation<GlobalSettings, Partial<GlobalSettings>>({
      query: (settings) => ({
        url: 'admin/settings',
        method: 'PUT',
        body: settings,
      }),
      invalidatesTags: ['GlobalSettings'],
    }),

    getIdentityOptions: build.query<IdentityOptions, void>({
      query: () => 'user-options/identity-options',
      providesTags: ['IdentityOptions'],
    }),
    updateIdentityOptions: build.mutation<IdentityOptions, IdentityOptions>({
      query: (options) => ({
        url: 'user-options/identity-options',
        method: 'PUT',
        body: options,
      }),
      invalidatesTags: ['IdentityOptions'],
    }),
    getMockNeighborhoods: build.query<Neighborhood[], void>({
      query: () => 'admin/mock/neighborhoods',
    }),
    getLocationCounts: build.query<{
      NEIGHBORHOOD: number;
      CITY: number;
      STATE: number;
      COUNTRY: number;
      TOTAL: number;
    }, void>({
      query: () => 'admin/mock/location-counts',
    }),
    testCreateNotification: build.mutation<NotificationTestResponse, void>({
      query: () => ({
        url: 'notifications/test-create',
        method: 'POST',
      }),
      invalidatesTags: ['Notifications'],
    }),
    testAllNotifications: build.mutation<NotificationTestResponse, void>({
      query: () => ({
        url: 'notifications/test-all',
        method: 'POST',
      }),
      invalidatesTags: ['Notifications'],
    }),
    getUserNotifications: build.query<NotificationTestResponse, { page?: number; limit?: number; unreadOnly?: boolean }>({
      query: (params) => ({
        url: 'notifications',
        params,
      }),
      providesTags: ['Notifications'],
    }),
    getUnreadCount: build.query<NotificationTestResponse, void>({
      query: () => 'notifications/unread-count',
      providesTags: ['Notifications'],
    }),
    markNotificationsAsRead: build.mutation<NotificationTestResponse, string[]>({
      query: (notificationIds) => ({
        url: 'notifications/mark-read',
        method: 'POST',
        body: { notificationIds },
      }),
      invalidatesTags: ['Notifications'],
    }),
    markNotificationsAsUnread: build.mutation<NotificationTestResponse, string[]>({
      query: (notificationIds) => ({
        url: 'notifications/mark-unread',
        method: 'POST',
        body: { notificationIds },
      }),
      invalidatesTags: ['Notifications'],
    }),
    markAllNotificationsAsRead: build.mutation<NotificationTestResponse, void>({
      query: () => ({
        url: 'notifications/mark-all-read',
        method: 'POST',
      }),
      invalidatesTags: ['Notifications'],
    }),
    deleteNotifications: build.mutation<NotificationTestResponse, string[]>({
      query: (notificationIds) => ({
        url: 'notifications/delete',
        method: 'POST',
        body: { notificationIds },
      }),
      invalidatesTags: ['Notifications'],
    }),
    updatePushToken: build.mutation<NotificationTestResponse, { token: string }>({
      query: (body) => ({
        url: 'notifications/update-push-token',
        method: 'POST',
        body,
      }),
    }),

    updateUser: build.mutation<User, { id: string; updates: Partial<User> }>({
      query: ({ id, updates }) => {
        return {
          url: `users/admin/users/${id}`,
          method: 'PUT',
          body: updates,
        };
      },
      invalidatesTags: ['Users'],
      transformResponse: (response: any) => {
        if (response.settings && response.settings instanceof Map) {
          response.settings = Object.fromEntries(response.settings);
        }
        return response;
      },
    }),
    deleteUser: build.mutation<{ message: string; deletedPostsCount: number }, string>({
      query: (userId) => ({
        url: `users/admin/users/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Users'],
    }),
    getExpiredPostsCount: build.query<{ count: number }, void>({
      query: () => 'posts/admin/expired-posts/count',
      providesTags: ['Posts'],
    }),
    getDeletedPostsCount: build.query<{ count: number }, void>({
      query: () => 'posts/admin/deleted-posts/count',
      providesTags: ['Posts'],
    }),
    getOrphanedCommentsCount: build.query<{ count: number }, void>({
      query: () => 'posts/admin/orphaned-comments/count',
      providesTags: ['Posts'],
    }),
    getPostCounts: build.query<{
      total: number;
      deleted: number;
      expired: number;
      comments: number;
      active: number;
      timestamp: string;
      currentDate: string;
    }, void>({
      query: () => 'posts/admin/posts-count',
      providesTags: ['Posts'],
    }),
    deleteExpiredPosts: build.mutation<{ success: boolean; message: string; deletedCount: number }, void>({
      query: () => ({
        url: 'posts/admin/expired-posts',
        method: 'DELETE',
      }),
      invalidatesTags: ['Posts'],
    }),
    deleteDeletedPosts: build.mutation<{ success: boolean; message: string; deletedCount: number }, void>({
      query: () => ({
        url: 'posts/admin/deleted-posts',
        method: 'DELETE',
      }),
      invalidatesTags: ['Posts'],
    }),
    deleteOrphanedComments: build.mutation<{ success: boolean; message: string; deletedCount: number }, void>({
      query: () => ({
        url: 'posts/admin/orphaned-comments',
        method: 'DELETE',
      }),
      invalidatesTags: ['Posts'],
    }),
    deleteMockPosts: build.mutation<{ success: boolean; message: string; deletedCount: number }, void>({
      query: () => ({
        url: 'posts/admin/mock-posts',
        method: 'DELETE',
      }),
      invalidatesTags: ['Posts'],
    }),
    updatePost: build.mutation<Post, { id: string; updates: Partial<Post> }>({
      query: ({ id, updates }) => ({
        url: `posts/admin/${id}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: ['Posts'],
    }),
    createLocation: build.mutation<any, any>({
      query: (locationData) => ({
        url: 'locations/admin',
        method: 'POST',
        body: locationData,
      }),
      invalidatesTags: ['Locations'],
    }),
    updateLocation: build.mutation<any, { id: string; updates: any }>({
      query: ({ id, updates }) => ({
        url: `locations/admin/${id}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: ['Locations'],
    }),
    updatePlace: build.mutation<any, { id: string; updates: any }>({
      query: ({ id, updates }) => ({
        url: `places/admin/${id}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: ['Locations'],
    }),
    deleteLocation: build.mutation<any, { id: string; level: string }>({
      query: ({ id, level }) => ({
        url: level === 'Place' ? `places/admin/${id}` : `locations/admin/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Locations'],
    }),
    uploadLocationPhoto: build.mutation<{ photoUrl: string }, { photo: File; locationId: string }>({
      query: ({ photo, locationId }) => {
        const formData = new FormData();
        formData.append('photo', photo);
        formData.append('locationId', locationId);
        return {
          url: 'upload/upload-location-photo',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Locations'],
    }),
    getUsers: build.query<any[], void>({
      query: () => 'users/admin/users',
      providesTags: ['Users'],
    }),
    // Admin raw user — facePicture/publicPicture as stored (not privacy-resolved).
    // Do NOT use GET users/:id here; that strips both pics for non-self viewers.
    getUserById: build.query<User, string>({
      query: (userId) => `users/admin/users/${userId}`,
      providesTags: (_result, _error, userId) => [{ type: 'Users', id: userId }],
    }),
    getPlaces: build.query<Place[], void>({
      query: () => 'admin/places',
      providesTags: ['Places'],
    }),
    // Pulse Simulator
    getPulseSimulatorNeighborhoods: build.query<Array<{ _id: string; name: string }>, void>({
      query: () => 'admin/pulse-simulator/neighborhoods',
    }),
    getPulseSimulatorPlaces: build.query<
      { places: Array<{ _id: string; name: string; type: string; coordinates?: { lat: number; lng: number } }> },
      { neighborhoodName?: string; latitude?: number; longitude?: number; radiusMeters?: number; limit?: number }
    >({
      query: ({ neighborhoodName = '', latitude, longitude, radiusMeters, limit = 30 }) => ({
        url: 'admin/pulse-simulator/places',
        params: {
          neighborhoodName,
          ...(latitude != null && { latitude }),
          ...(longitude != null && { longitude }),
          ...(radiusMeters != null && radiusMeters > 0 && { radiusMeters }),
          limit,
        },
      }),
    }),
    getPulseSimulatorActivePulses: build.query<
      { pulses: Array<{ _id: string; place: string; word: string }> },
      string[]
    >({
      query: (placeIds) => ({
        url: 'admin/pulse-simulator/active-pulses',
        params: { placeIds: placeIds.join(',') },
      }),
    }),
    clearPulseSimulatorPulses: build.mutation<{ deletedCount: number }, void>({
      query: () => ({
        url: 'admin/pulse-simulator/clear',
        method: 'DELETE',
      }),
    }),
    getLocations: build.query<Location[], void>({
      query: () => 'locations/admin/all',
      providesTags: ['Locations'],
    }),
    getLocationDetails: build.query<Location, string>({
      query: (id) => `locations/admin/inspect/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Locations', id }],
    }),
    getPlaceDetails: build.query<any, string>({
      query: (id) => `places/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Places', id }],
    }),
    updateLocationBounds: build.mutation<{ message: string; location: Location }, { id: string; boundingBox: any }>({
      query: ({ id, boundingBox }) => ({
        url: `locations/admin/bounds/${id}`,
        method: 'PUT',
        body: { bounds: boundingBox },
      }),
      invalidatesTags: ['Locations'],
    }),
    updatePlaceBounds: build.mutation<{ message: string; place: any }, { id: string; boundingBox: any }>({
      query: ({ id, boundingBox }) => ({
        url: `places/admin/bounds/${id}`,
        method: 'PUT',
        body: { bounds: boundingBox },
      }),
      invalidatesTags: ['Locations'], // Places are included in the locations list
    }),
    updateLocationCenterPoint: build.mutation<{ success: boolean; message: string; location: Location }, { id: string; coordinates: [number, number] }>({
      query: ({ id, coordinates }) => ({
        url: `locations/admin/center-point/${id}`,
        method: 'PUT',
        body: { coordinates },
      }),
      invalidatesTags: ['Locations'],
    }),
    getFavoriteLocations: build.query<{ success: boolean; favoriteLocations: string[] }, void>({
      query: () => 'locations/admin/favorites',
      providesTags: ['Locations'],
    }),
    addFavoriteLocation: build.mutation<{ success: boolean; favoriteLocations: string[] }, string>({
      query: (locationId) => ({
        url: `locations/admin/favorites/${locationId}`,
        method: 'POST',
      }),
      invalidatesTags: ['Locations'],
    }),
    removeFavoriteLocation: build.mutation<{ success: boolean; favoriteLocations: string[] }, string>({
      query: (locationId) => ({
        url: `locations/admin/favorites/${locationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Locations'],
    }),
    getAllPosts: build.query<{ data: Post[]; total: number }, {
      page?: number;
      limit?: number;
      locationId?: string;
      excludeComments?: boolean;
      excludeExpired?: boolean;
      includeDeleted?: boolean;
      userFilter?: string[];
      mediaFilter?: string[];
      originLevelFilter?: string[];
      currentPostLevelFilter?: string[];
      locationNameFilter?: string[];
    }>({
      query: (params) => ({
        url: 'posts/admin/all-posts',
        params: {
          page: params?.page ?? 0,
          limit: params?.limit ?? 50,
          ...(params.locationId && { locationId: params.locationId }),
          ...(params.excludeComments && { excludeComments: params.excludeComments }),
          ...(params.excludeExpired && { excludeExpired: params.excludeExpired }),
          ...(params.userFilter && params.userFilter.length > 0 && { userFilter: params.userFilter.join(',') }),
          ...(params.mediaFilter && params.mediaFilter.length > 0 && { mediaFilter: params.mediaFilter.join(',') }),
          ...(params.originLevelFilter && params.originLevelFilter.length > 0 && { originLevelFilter: params.originLevelFilter.join(',') }),
          ...(params.currentPostLevelFilter && params.currentPostLevelFilter.length > 0 && { currentPostLevelFilter: params.currentPostLevelFilter.join(',') }),
          ...(params.locationNameFilter && params.locationNameFilter.length > 0 && { locationNameFilter: params.locationNameFilter.join(',') }),
          ...(params.includeDeleted && { includeDeleted: params.includeDeleted })
        }
      }),
      providesTags: ['Posts'],
    }),
    getPostFilterValues: build.query<{
      user: string[];
      media: string[];
      originLevel: string[];
      currentPostLevel: string[];
      location: string[];
    }, {
      excludeComments?: boolean;
      excludeExpired?: boolean;
      includeDeleted?: boolean;
    }>({
      query: (params) => ({
        url: 'posts/admin/filter-values',
        params: {
          ...(params.excludeComments && { excludeComments: params.excludeComments }),
          ...(params.excludeExpired && { excludeExpired: params.excludeExpired }),
          ...(params.includeDeleted && { includeDeleted: params.includeDeleted }),
        },
      }),
      providesTags: ['Posts'],
    }),
    deletePost: build.mutation<{ success: boolean; message: string }, string>({
      query: (postId) => ({
        url: `posts/admin/posts/${postId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Posts'],
    }),
    bulkDeletePosts: build.mutation<{ 
      success: boolean; 
      message: string; 
      deletedCount: number; 
      errors: string[] 
    }, string[]>({
      query: (postIds) => ({
        url: 'posts/admin/posts/bulk-delete',
        method: 'POST',
        body: { postIds },
      }),
      invalidatesTags: ['Posts'],
    }),
    getAllUsers: build.query<User[], void>({
      query: () => {
        return 'users/admin/users';
      },
      providesTags: ['Users'],
      transformResponse: (response: User[]) => {
        // Sort users alphabetically by displayName
        const sorted = [...response].sort((a, b) => 
          (a.displayName || '').localeCompare(b.displayName || '')
        );
        return sorted;
      },
    }),
    getAllLocations: build.query<Location[], void>({
      query: () => 'locations/admin/all',
      providesTags: ['Locations'],
    }),
    getPostStats: build.query<PostStats, { 
      locationId?: string;
      excludeComments?: boolean;
      excludeExpired?: boolean;
      includeDeleted?: boolean;
    }>({
      query: (params) => ({
        url: 'posts/admin/stats',
        params: {
          ...(params.locationId && { locationId: params.locationId }),
          ...(params.excludeComments && { excludeComments: params.excludeComments }),
          ...(params.excludeExpired && { excludeExpired: params.excludeExpired }),
          ...(params.includeDeleted && { includeDeleted: params.includeDeleted })
        }
      }),
      providesTags: ['Posts'],
    }),
    getPostCountByLocation: build.query<{ count: number }, string>({
      query: (locationId) => ({
        url: 'posts/admin/post-count-by-location',
        params: { locationId }
      }),
      providesTags: ['Posts'],
    }),
    getPostCountsByLocations: build.mutation<Record<string, number>, string[]>({
      query: (locationIds) => ({
        url: 'posts/admin/post-counts-by-locations',
        method: 'POST',
        body: { locationIds }
      }),
      invalidatesTags: ['Posts'],
    }),
    createMockPosts: build.mutation<CreateMockPostsResponse, CreateMockPostsRequest>({
      query: (body) => ({
        url: 'admin/mock/posts',
        method: 'POST',
        body,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          console.error('Error creating mock posts:', error);
        }
      },
    }),
    createCustomPost: build.mutation<{ success: boolean; message: string; post: Post }, FormData>({
      query: (formData) => ({
        url: 'admin/mock/custom-post',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Posts'],
    }),
    cancelMockPostCreation: build.mutation<{ success: boolean; message: string; progress?: { current: number; total: number } }, void>({
      query: () => ({
        url: 'admin/mock/posts/cancel',
        method: 'POST',
      }),
    }),
    getActiveMockPostJobs: build.query<{ success: boolean; jobs: MockPostJobStatus[] }, void>({
      query: () => 'admin/mock/posts/jobs',
      providesTags: ['MockPosts'],
    }),
    getMockPostJobStatus: build.query<{ success: boolean; job: MockPostJobStatus }, string>({
      query: (jobId) => `admin/mock/posts/jobs/${jobId}`,
      providesTags: ['MockPosts'],
    }),
    cancelMockPostJob: build.mutation<{ success: boolean; message: string; job?: MockPostJobStatus }, string>({
      query: (jobId) => ({
        url: `admin/mock/posts/jobs/${jobId}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: ['MockPosts'],
    }),
    refreshToken: build.mutation<
      { accessToken: string; refreshToken: string },
      { refreshToken: string }
    >({
      query: (credentials) => ({
        url: 'auth/refresh-token',
        method: 'POST',
        body: credentials,
      }),
    }),
    getPlaceTypes: build.query<Record<string, string[]>, void>({
      query: () => 'places/types',
    }),
    importGooglePlaces: build.mutation<{
      success: boolean;
      stats: {
        totalPlacesFound: number;
        totalPlacesImported: number;
        totalPlacesSkipped: number;
        totalErrors: number;
        costBreakdown: {
          nearbySearchCost: number;
          placeDetailsCost: number;
          totalCost: number;
        };
      };
      errors: string[];
      message: string;
    }, {
      latitude: number;
      longitude: number;
      radiusMeters: number;
      includedTypes?: string[];
      maxResults?: number;
    }>({
      query: (body) => ({
        url: 'admin/google-places/import',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Places', 'Locations'],
    }),

    // OSM Search (free, no API key)
    searchOSMPlaces: build.query<{
      success: boolean;
      places: Array<{
        id: string;
        name: string;
        type: string;
        coordinates: { lat: number; lng: number };
        address?: string;
        tags?: Record<string, string>;
        isDuplicate?: boolean;
      }>;
      count: number;
      duplicatesCount?: number;
    }, {
      latitude: number;
      longitude: number;
      radiusMeters: number;
      placeTypes?: string[];
      limit?: number;
    }>({
      query: (params) => ({
        url: 'admin/google-places/search-osm',
        params: {
          latitude: params.latitude.toString(),
          longitude: params.longitude.toString(),
          radiusMeters: params.radiusMeters.toString(),
          ...(params.placeTypes && params.placeTypes.length > 0 && { placeTypes: params.placeTypes }),
          ...(params.limit != null && { limit: params.limit.toString() }),
        },
      }),
    }),
    // Google Places Search (costs money)
    searchGooglePlaces: build.query<{
      success: boolean;
      places: Array<{
        id: string;
        name: string;
        type: string;
        coordinates: { lat: number; lng: number };
        address?: string;
        isDuplicate?: boolean;
      }>;
      count: number;
      duplicatesCount?: number;
    }, {
      latitude: number;
      longitude: number;
      radiusMeters: number;
      includedTypes?: string[];
    }>({
      query: (params) => ({
        url: 'admin/google-places/search-google',
        params: {
          latitude: params.latitude.toString(),
          longitude: params.longitude.toString(),
          radiusMeters: params.radiusMeters.toString(),
          ...(params.includedTypes && params.includedTypes.length > 0 && { includedTypes: params.includedTypes }),
        },
      }),
    }),

    // Import selected places from OSM results
    importSelectedPlaces: build.mutation<{
      success: boolean;
      stats: {
        totalPlacesFound: number;
        totalPlacesImported: number;
        totalPlacesSkipped: number;
        totalErrors: number;
        costBreakdown: {
          nearbySearchCost: number;
          placeDetailsCost: number;
          photoCost: number;
          totalCost: number;
        };
      };
      results: Array<{
        name: string;
        success: boolean;
        reason?: string;
        googlePlaceId?: string;
      }>;
      errors?: string[];
    }, {
      places: Array<{
        name: string;
        latitude: number;
        longitude: number;
      }>;
    }>({
      query: (body) => ({
        url: 'admin/google-places/import-selected',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Places'],
    }),
    getSavedMockPosts: build.query<
      {
        _id: string;
        text: string;
        locationId: string;
        locationType: 'place' | 'neighborhood' | 'place-type';
        allowMedia?: boolean;
        media?: {
          type: 'photo' | 'video';
          url: string;
          thumbnailUrl?: string;
        };
      }[],
      void
    >({
      query: () => 'admin/mock/saved-posts',
      providesTags: ['MockPosts']
    }),
    saveMockPostText: build.mutation<
      { success: boolean; message: string },
      {
        text: string;
        locationId: string;
        locationType: 'place' | 'neighborhood' | 'place-type';
        allowMedia?: boolean;
      }
    >({
      query: (postData) => ({
        url: 'admin/mock/save-post-text',
        method: 'POST',
        body: postData
      }),
      invalidatesTags: ['MockPosts']
    }),
    uploadMockPostMedia: build.mutation<
      { success: boolean; message: string; media: { type: 'photo' | 'video'; url: string; thumbnailUrl?: string } },
      { postId: string; file: File }
    >({
      query: ({ postId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('postId', postId);
        
        return {
          url: 'admin/mock/upload-post-media',
          method: 'POST',
          body: formData
        };
      },
      invalidatesTags: ['MockPosts']
    }),
    deleteMockPostMedia: build.mutation<
      { success: boolean; message: string },
      string // postId
    >({
      query: (postId) => ({
        url: `admin/mock/delete-post-media/${postId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['MockPosts']
    }),
    deleteMockPostText: build.mutation<
      { success: boolean; message: string },
      string
    >({
      query: (postId) => ({
        url: `admin/mock/delete-post-text/${postId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['MockPosts']
    }),
    updateMockPostAllowMedia: build.mutation<
      { success: boolean; message: string },
      { postId: string; allowMedia: boolean }
    >({
      query: ({ postId, allowMedia }) => ({
        url: `admin/mock/update-post-allow-media/${postId}`,
        method: 'PATCH',
        body: { allowMedia }
      }),
      invalidatesTags: ['MockPosts']
    }),
    
    // User Profile Migration Tools
    backupUserProfiles: build.mutation<
      { success: boolean; message: string; filename: string },
      { filename: string }
    >({
      query: (body) => ({
        url: 'admin/users/backup',
        method: 'POST',
        body
      })
    }),
    
    migrateUserProfiles: build.mutation<
      { 
        success: boolean; 
        message: string; 
        progress: { current: number; total: number; }; 
        changes?: { 
          added: number; 
          modified: number; 
          skipped: number; 
          errors: number; 
        };
        detailedReport?: {
          totals: {
            processed: number;
            modified: number;
            skipped: number;
            errors: number;
          };
          migrations: {
            profilePictureMigrated: number;
            altProfilePictureMigrated: number;
            profilePictureCleared: number;
            altProfilePictureCleared: number;
          };
          skippedDetails: {
            alreadyMigrated: number;
            noChangesNeeded: number;
          };
        };
        dryRun: boolean;
      },
      { batchSize: number; dryRun: boolean }
    >({
      query: (body) => ({
        url: 'admin/users/migrate-profile-pictures',
        method: 'POST',
        body
      })
    }),
    
    ensureSchemaFields: build.mutation<
      { 
        success: boolean; 
        message: string;
        stats: {
          usersWithoutFacePicture: number;
          usersWithoutPublicPicture: number;
          matchedCount: number;
          modifiedCount: number;
        }
      },
      void
    >({
      query: () => ({
        url: 'admin/users/ensure-schema-fields',
        method: 'POST'
      })
    }),
    
    // Mock User Creation
    getAvailableTags: build.query<string[], void>({
      query: () => 'admin/mock/available-tags'
    }),
    
    getNYCNeighborhoods: build.query<
      Array<{
        _id: string;
        name: string;
        coordinates: {
          type: string;
          coordinates: [number, number];
        };
      }>,
      void
    >({
      query: () => 'admin/mock/nyc-neighborhoods'
    }),
    
    createMockUser: build.mutation<
      { success: boolean; message: string; user: any },
      {
        displayName: string;
        username: string;
        email: string;
        profilePicture: string;
        publicPicture: string;
        gender: string;
        race: string;
        sexualOrientation: string;
        tags: string[];
        homeNeighborhood: string;
        bio: string;
      }
    >({
      query: (userData) => ({
        url: 'admin/mock/create-user',
        method: 'POST',
        body: userData
      }),
      invalidatesTags: ['Users']
    }),
    
    getProfilePicture: build.query<Blob, string>({
      query: (filename) => ({
        url: `admin/mock/profile-picture/${filename}`,
        responseHandler: (response) => response.blob()
      })
    }),
    
    // Upload user photos
    uploadUserFacePicture: build.mutation<{ message: string; url: string; filename: string }, { userId: string; photo: File }>({
      query: ({ userId, photo }) => {
        const formData = new FormData();
        formData.append('file', photo);
        return {
          url: `users/admin/users/${userId}/upload-face-picture`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Users'],
    }),
    
    uploadUserPublicPicture: build.mutation<{ message: string; url: string; filename: string }, { userId: string; photo: File }>({
      query: ({ userId, photo }) => {
        const formData = new FormData();
        formData.append('file', photo);
        return {
          url: `users/admin/users/${userId}/upload-public-picture`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Users'],
    }),
    
    // Update password for current user
    updatePassword: build.mutation<{ message: string }, { currentPassword: string; newPassword: string }>({
      query: (body) => ({
        url: 'auth/update-password',
        method: 'POST',
        body,
      }),
    }),
    
    // Update user profile (displayName, username, email)
    updateProfile: build.mutation<any, { displayName?: string; username?: string; email?: string }>({
      query: (body) => ({
        url: 'users/update-profile',
        method: 'PUT',
        body,
      }),
    }),
    
    // Get current user profile
    getCurrentUserProfile: build.query<any, void>({
      query: () => 'users/profile',
    }),

    // Security/Violation endpoints
    getViolationStats: build.query<any, { timeframe?: 'day' | 'week' | 'month' }>({
      query: (params) => ({
        url: 'locations/violations/admin/stats',
        params,
      }),
      providesTags: ['Security'],
    }),
    getAllViolations: build.query<any, { 
      page?: number; 
      limit?: number; 
      violationType?: string; 
      severity?: string; 
      resolved?: boolean; 
      userId?: string;
    }>({
      query: (params) => ({
        url: 'locations/violations/admin/all',
        params,
      }),
      providesTags: ['Security'],
    }),
    getHighRiskUsers: build.query<any, { limit?: number }>({
      query: (params) => ({
        url: 'locations/violations/admin/high-risk-users',
        params,
      }),
      providesTags: ['Security'],
    }),
    resolveViolation: build.mutation<any, { violationId: string; reason: string }>({
      query: ({ violationId, reason }) => ({
        url: `locations/violations/admin/${violationId}/resolve`,
        method: 'PUT',
        body: { reason },
      }),
      invalidatesTags: ['Security'],
    }),
    cleanupOldViolations: build.mutation<any, { daysOld?: number }>({
      query: (body) => ({
        url: 'locations/violations/admin/cleanup',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Security'],
    }),

    // Product analytics
    getAnalyticsOverview: build.query<any, Record<string, string | undefined>>({
      query: (params) => ({ url: 'admin/analytics/overview', params }),
      providesTags: ['Analytics'],
    }),
    getAnalyticsFunnel: build.query<any, Record<string, string | undefined>>({
      query: (params) => ({ url: 'admin/analytics/funnel', params }),
      providesTags: ['Analytics'],
    }),
    getAnalyticsContent: build.query<any, Record<string, string | undefined>>({
      query: (params) => ({ url: 'admin/analytics/content', params }),
      providesTags: ['Analytics'],
    }),
    getAnalyticsSocial: build.query<any, Record<string, string | undefined>>({
      query: (params) => ({ url: 'admin/analytics/social', params }),
      providesTags: ['Analytics'],
    }),
    getAnalyticsRetention: build.query<any, Record<string, string | undefined>>({
      query: (params) => ({ url: 'admin/analytics/retention', params }),
      providesTags: ['Analytics'],
    }),
    getAnalyticsGeography: build.query<any, Record<string, string | undefined>>({
      query: (params) => ({ url: 'admin/analytics/geography', params }),
      providesTags: ['Analytics'],
    }),
    getAnalyticsCohorts: build.query<any, void>({
      query: () => 'admin/analytics/cohorts',
      providesTags: ['Analytics'],
    }),
    getAnalyticsGrowth: build.query<any, Record<string, string | undefined>>({
      query: (params) => ({ url: 'admin/analytics/growth', params }),
      providesTags: ['Analytics'],
    }),
    getAnalyticsUserPosts: build.query<any, string>({
      query: (userId) => `admin/analytics/users/${userId}/posts`,
      providesTags: ['Analytics'],
    }),
    getAnalyticsPostsInRange: build.query<any, Record<string, string | undefined>>({
      query: (params) => ({ url: 'admin/analytics/posts', params }),
      providesTags: ['Analytics'],
    }),
    getAnalyticsEngagement: build.query<any, Record<string, string | undefined>>({
      query: (params) => ({ url: 'admin/analytics/engagement', params }),
      providesTags: ['Analytics'],
    }),
    updateUserAcquisition: build.mutation<
      any,
      {
        userId: string;
        userCohort?: string;
        acquisitionSource?: string;
        acquisitionDetail?: string;
      }
    >({
      query: ({ userId, ...body }) => ({
        url: `admin/analytics/users/${userId}/acquisition`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Analytics', 'Users'],
    }),

    getPlaceClaims: build.query<
      { items: any[]; total: number; limit: number; skip: number },
      { status?: string; limit?: number; skip?: number }
    >({
      query: (params) => ({ url: 'admin/place-claims', params }),
      providesTags: ['PlaceClaims'],
    }),
    getPlaceClaim: build.query<{ claim: any }, string>({
      query: (id) => `admin/place-claims/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'PlaceClaims', id }],
    }),
    approvePlaceClaim: build.mutation<{ claim: any }, { id: string; adminNote?: string }>({
      query: ({ id, adminNote }) => ({
        url: `admin/place-claims/${id}/approve`,
        method: 'POST',
        body: { adminNote },
      }),
      invalidatesTags: ['PlaceClaims', 'Places'],
    }),
    rejectPlaceClaim: build.mutation<{ claim: any }, { id: string; reason?: string }>({
      query: ({ id, reason }) => ({
        url: `admin/place-claims/${id}/reject`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['PlaceClaims'],
    }),

    getWelcomeDm: build.query<
      {
        enabled: boolean;
        senderUserId: string;
        message: string;
        sender: { _id: string; username?: string; displayName?: string } | null;
      },
      void
    >({
      query: () => 'admin/welcome-dm',
      providesTags: ['WelcomeDm'],
    }),
    updateWelcomeDm: build.mutation<
      { enabled: boolean; senderUserId: string; message: string },
      { enabled?: boolean; senderUserId?: string; message?: string }
    >({
      query: (body) => ({
        url: 'admin/welcome-dm',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['WelcomeDm'],
    }),
    testWelcomeDm: build.mutation<{ sent: boolean; userId: string }, { userId: string }>({
      query: (body) => ({
        url: 'admin/welcome-dm/test',
        method: 'POST',
        body,
      }),
    }),
    getBroadcastPreview: build.query<
      { recipientCount: number },
      Record<string, string | undefined> | void
    >({
      query: (params) => ({
        url: 'admin/notifications/broadcast/preview',
        params: params || undefined,
      }),
    }),
    sendBroadcast: build.mutation<
      { attempted: number; sent: number; failed: number; skippedMocks: number },
      {
        message: string;
        title?: string;
        activeWithin?: string;
        signedUpWithin?: string;
        includeExcluded?: boolean;
        pushableOnly?: boolean;
        // Targeting: specific users and/or a location node.
        userIds?: string[];
        neighborhoodId?: string;
        cityId?: string;
        stateId?: string;
        // Deep link: where a tap on the notification navigates.
        deepLink?: string;
        deepLinkId?: string;
      }
    >({
      query: (body) => ({
        url: 'admin/notifications/broadcast',
        method: 'POST',
        body,
      }),
    }),
    getAudienceSummary: build.query<
      {
        active: Record<string, number>;
        signups: Record<string, number>;
        total: number;
        pushable: number;
      },
      Record<string, string | undefined> | void
    >({
      query: (params) => ({
        url: 'admin/audience/summary',
        params: params || undefined,
      }),
      providesTags: ['Analytics'],
    }),
    getAudienceMembers: build.query<
      { members: AudienceMember[]; total: number },
      { axis: 'active' | 'signup'; window: string; includeExcluded?: boolean; limit?: number }
    >({
      query: ({ axis, window, includeExcluded, limit }) => ({
        url: 'admin/audience/members',
        params: {
          axis,
          window,
          includeExcluded: includeExcluded ? 'true' : undefined,
          limit: limit != null ? String(limit) : undefined,
        },
      }),
      providesTags: ['Analytics'],
    }),
    getAuthEventSummary: build.query<Record<AuthEventWindow, AuthEventWindowStats>, void>({
      query: () => ({ url: 'admin/auth-events/summary' }),
      providesTags: ['Analytics'],
    }),
    getAuthAbusers: build.query<
      { abusers: AuthAbuser[]; window: AuthEventWindow },
      { window: AuthEventWindow; limit?: number; minCodes?: number }
    >({
      query: ({ window, limit, minCodes }) => ({
        url: 'admin/auth-events/abusers',
        params: {
          window,
          limit: limit != null ? String(limit) : undefined,
          minCodes: minCodes != null ? String(minCodes) : undefined,
        },
      }),
      providesTags: ['Analytics'],
    }),
    getRecentAuthEvents: build.query<
      { events: AuthEventRow[] },
      { limit?: number; phone?: string; type?: string } | void
    >({
      query: (params) => ({
        url: 'admin/auth-events/recent',
        params: params
          ? {
              limit: params.limit != null ? String(params.limit) : undefined,
              phone: params.phone || undefined,
              type: params.type || undefined,
            }
          : undefined,
      }),
      providesTags: ['Analytics'],
    }),

    // ==================== Enrichment entities ====================
    getEnrichedEntities: build.query<
      { items: EnrichedEntity[]; total: number; limit: number; skip: number },
      {
        search?: string;
        kind?: string;
        status?: string;
        source?: string;
        hasHooks?: boolean;
        reviewed?: boolean;
        limit?: number;
        skip?: number;
        sort?: string;
      } | void
    >({
      query: (params) => ({
        url: 'admin/enriched-entities',
        params: params
          ? {
              search: params.search || undefined,
              kind: params.kind || undefined,
              status: params.status || undefined,
              source: params.source || undefined,
              hasHooks: params.hasHooks ? 'true' : undefined,
              reviewed:
                params.reviewed === true
                  ? 'true'
                  : params.reviewed === false
                    ? 'false'
                    : undefined,
              limit: params.limit != null ? String(params.limit) : undefined,
              skip: params.skip != null ? String(params.skip) : undefined,
              sort: params.sort || undefined,
            }
          : undefined,
      }),
      providesTags: ['Enrichment'],
    }),
    getEnrichmentFacets: build.query<
      { kinds: string[]; sources: string[]; statuses: string[] },
      void
    >({
      query: () => 'admin/enriched-entities/facets',
    }),
    getEnrichedEntity: build.query<{ entity: EnrichedEntity }, string>({
      query: (id) => `admin/enriched-entities/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Enrichment', id }],
    }),
    updateEnrichedEntity: build.mutation<
      { entity: EnrichedEntity },
      { id: string; updates: Partial<EnrichedEntity> }
    >({
      query: ({ id, updates }) => ({
        url: `admin/enriched-entities/${id}`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: ['Enrichment'],
    }),
    deleteEnrichedEntity: build.mutation<void, string>({
      query: (id) => ({
        url: `admin/enriched-entities/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Enrichment'],
    }),

    // ── Interest copy (fixed catalog openers) ──────────────────────────────
    getInterestCatalog: build.query<InterestCatalog, void>({
      query: () => 'admin/interest-copy/catalog',
    }),
    getInterestCopy: build.query<
      { items: InterestCopyRow[] },
      { scope?: InterestCopyScope; reviewed?: boolean; search?: string } | void
    >({
      query: (args) => {
        const params = new URLSearchParams();
        if (args && args.scope) params.set('scope', args.scope);
        if (args && typeof args.reviewed === 'boolean') params.set('reviewed', String(args.reviewed));
        if (args && args.search) params.set('search', args.search);
        const qs = params.toString();
        return `admin/interest-copy${qs ? `?${qs}` : ''}`;
      },
      providesTags: ['InterestCopy'],
    }),
    upsertInterestCopy: build.mutation<
      { item: InterestCopyRow },
      {
        scope: InterestCopyScope;
        key: string;
        displayLabel?: string;
        openers1v1?: string[];
        openersRoom?: string[];
        reviewed?: boolean;
      }
    >({
      query: (body) => ({ url: 'admin/interest-copy', method: 'PUT', body }),
      invalidatesTags: ['InterestCopy'],
    }),
    deleteInterestCopy: build.mutation<void, string>({
      query: (id) => ({ url: `admin/interest-copy/${id}`, method: 'DELETE' }),
      invalidatesTags: ['InterestCopy'],
    }),
  }),
});

export const {
  useGetGlobalSettingsQuery,
  useUpdateGlobalSettingsMutation,

  useGetIdentityOptionsQuery,
  useUpdateIdentityOptionsMutation,
  useGetMockNeighborhoodsQuery,
  useGetLocationCountsQuery,
  useCreateMockPostsMutation,
  useCreateCustomPostMutation,
  useGetActiveMockPostJobsQuery,
  useGetMockPostJobStatusQuery,
  useCancelMockPostJobMutation,
  useTestCreateNotificationMutation,
  useTestAllNotificationsMutation,
  useGetUserNotificationsQuery,
  useGetUnreadCountQuery,
  useMarkNotificationsAsReadMutation,
  useMarkNotificationsAsUnreadMutation,
  useMarkAllNotificationsAsReadMutation,
  useDeleteNotificationsMutation,
  useUpdatePushTokenMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetExpiredPostsCountQuery,
  useGetDeletedPostsCountQuery,
  useGetOrphanedCommentsCountQuery,
  useGetPostCountsQuery,
  useDeleteExpiredPostsMutation,
  useDeleteDeletedPostsMutation,
  useDeleteOrphanedCommentsMutation,
  useDeleteMockPostsMutation,
  useUpdatePostMutation,
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useUpdatePlaceMutation,
  useDeleteLocationMutation,
  useUploadLocationPhotoMutation,
  useGetUsersQuery,
  useGetUserByIdQuery,
  useGetPlacesQuery,
  useGetLocationsQuery,
  useGetLocationDetailsQuery,
  useGetPlaceDetailsQuery,
  useUpdateLocationBoundsMutation,
  useUpdatePlaceBoundsMutation,
  useUpdateLocationCenterPointMutation,
  useGetFavoriteLocationsQuery,
  useAddFavoriteLocationMutation,
  useRemoveFavoriteLocationMutation,
  useGetAllPostsQuery,
  useGetPostFilterValuesQuery,
  useGetAllUsersQuery,
  useGetAllLocationsQuery,
  useDeletePostMutation,
  useBulkDeletePostsMutation,
  useGetPostStatsQuery,
  useGetPostCountByLocationQuery,
  useGetPostCountsByLocationsMutation,
  useGetPlaceTypesQuery,
  useImportGooglePlacesMutation,
  useSearchOSMPlacesQuery,
  useLazySearchOSMPlacesQuery,
  useSearchGooglePlacesQuery,
  useLazySearchGooglePlacesQuery,
  useImportSelectedPlacesMutation,
  useCancelMockPostCreationMutation,
  useGetSavedMockPostsQuery,
  useSaveMockPostTextMutation,
  useDeleteMockPostTextMutation,
  useUploadMockPostMediaMutation,
  useDeleteMockPostMediaMutation,
  useUpdateMockPostAllowMediaMutation,
  useBackupUserProfilesMutation,
  useMigrateUserProfilesMutation,
  useEnsureSchemaFieldsMutation,
  useGetAvailableTagsQuery,
  useGetNYCNeighborhoodsQuery,
  useCreateMockUserMutation,
  useGetProfilePictureQuery,
  useUploadUserFacePictureMutation,
  useUploadUserPublicPictureMutation,
  useUpdatePasswordMutation,
  useUpdateProfileMutation,
  useGetCurrentUserProfileQuery,
  useGetViolationStatsQuery,
  useGetAllViolationsQuery,
  useGetHighRiskUsersQuery,
  useResolveViolationMutation,
  useCleanupOldViolationsMutation,
  useGetAnalyticsOverviewQuery,
  useGetAnalyticsFunnelQuery,
  useGetAnalyticsContentQuery,
  useGetAnalyticsSocialQuery,
  useGetAnalyticsRetentionQuery,
  useGetAnalyticsGeographyQuery,
  useGetAnalyticsCohortsQuery,
  useGetAnalyticsGrowthQuery,
  useLazyGetAnalyticsUserPostsQuery,
  useLazyGetAnalyticsPostsInRangeQuery,
  useGetAnalyticsEngagementQuery,
  useUpdateUserAcquisitionMutation,
  useGetPlaceClaimsQuery,
  useGetPlaceClaimQuery,
  useApprovePlaceClaimMutation,
  useRejectPlaceClaimMutation,
  useGetWelcomeDmQuery,
  useUpdateWelcomeDmMutation,
  useTestWelcomeDmMutation,
  useGetBroadcastPreviewQuery,
  useSendBroadcastMutation,
  useGetAudienceSummaryQuery,
  useLazyGetAudienceMembersQuery,
  useGetAuthEventSummaryQuery,
  useGetAuthAbusersQuery,
  useGetRecentAuthEventsQuery,
  useGetEnrichedEntitiesQuery,
  useGetEnrichmentFacetsQuery,
  useGetEnrichedEntityQuery,
  useUpdateEnrichedEntityMutation,
  useDeleteEnrichedEntityMutation,
  useGetInterestCatalogQuery,
  useGetInterestCopyQuery,
  useUpsertInterestCopyMutation,
  useDeleteInterestCopyMutation,
} = adminApi;